import React, { useContext } from "react";
import { motion } from "framer-motion";
import StyleContext from "../../contexts/StyleContext";
import { bigProjects } from "../../portfolio";

export default function StartupProjectModern() {
  const { isDark } = useContext(StyleContext);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div className="relative py-20 md:py-32 overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-violet-50 to-purple-50 dark:from-gray-900 dark:via-violet-900/20 dark:to-purple-900/20" />

      {/* Floating Orb */}
      <motion.div
        className="absolute left-0 bottom-1/4 w-96 h-96 bg-gradient-to-r from-violet-400 to-purple-400 rounded-full opacity-20 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
          y: [0, -50, 0],
        }}
        transition={{
          duration: 11,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            className="inline-block mb-4"
            whileHover={{ scale: 1.05 }}
          >
            <span className="px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-full text-sm font-semibold shadow-lg">
              🚀 Featured Work
            </span>
          </motion.div>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 ${
            isDark ? "text-white" : "text-gray-900"
          }`}>
            {bigProjects.title}
          </h2>
          <p className={`text-lg md:text-xl max-w-3xl mx-auto ${
            isDark ? "text-gray-400" : "text-gray-600"
          }`}>
            {bigProjects.subtitle}
          </p>
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto"
        >
          {bigProjects.projects.map((project, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{
                y: -10,
                transition: { duration: 0.3 }
              }}
              className="glass backdrop-blur-xl rounded-3xl overflow-hidden bg-white/60 dark:bg-gray-800/60 border border-white/20 dark:border-gray-700/20 shadow-xl hover:shadow-2xl hover:shadow-violet-500/20 transition-all duration-300"
            >
              {/* Project Image */}
              <div className="relative h-56 overflow-hidden bg-gradient-to-br from-violet-500/20 to-purple-500/20">
                <motion.img
                  src={project.image.default || project.image}
                  alt={project.name}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.4 }}
                />
                {/* Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                {/* Project Badge */}
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-full text-xs font-semibold text-violet-600 dark:text-violet-400">
                    Featured
                  </span>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-6">
                <h3 className={`text-2xl font-bold mb-3 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}>
                  {project.name}
                </h3>

                <p className={`mb-6 leading-relaxed ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}>
                  {project.description}
                </p>

                {/* Links */}
                {project.link && project.link.length > 0 && (
                  <div className="flex flex-wrap gap-3">
                    {project.link.map((link, linkIndex) => (
                      <motion.a
                        key={linkIndex}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-lg font-medium shadow-lg hover:shadow-violet-500/50 transition-all duration-300 flex items-center gap-2"
                      >
                        <span>{link.name}</span>
                        <svg className="w-4 h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                          <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </motion.a>
                    ))}
                  </div>
                )}
              </div>

              {/* Bottom Accent */}
              <div className="h-1 bg-gradient-to-r from-violet-500 to-purple-500" />
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom Decorative Element */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-20 flex justify-center"
        >
          <div className="flex items-center gap-2">
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-violet-500 to-transparent" />
            <div className="w-2 h-2 rounded-full bg-violet-500" />
            <div className="h-px w-20 bg-gradient-to-r from-transparent via-purple-500 to-transparent" />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
